import { windowsArmReinstallRequired } from '@api/devices/agent-platform';
import { DeviceActivityPreview } from '../devices/DeviceActivity';
import type { JobView, RunbookSummary } from '@api/devices/contracts';
import { runbookUnavailableReason } from '@api/operations/runbook-compatibility';
import type { SearchHit } from '@contracts';
import { ChevronRight, Layers, MoreHorizontal, Play, PlugZap, Sparkles, Terminal, Trash2 } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { FactsPanel, ResolvedList, SignalCard, Vitals } from '../devices/DeviceOverview';
import { ActivityPanel, PerformancePanel, PowerShellPanel, SoftwarePanel, StoragePanel } from '../devices/DevicePanels';
import { RemoveDeviceDialog } from '../devices/RemoveDeviceDialog';
import { DeviceName } from '../devices/DeviceName';
import { DeviceIdentity } from '../devices/DeviceIdentity';
import { RunbookDialog } from '../devices/RunbookDialog';
import { ReviewControls, ReviewedList } from '../devices/SignalReview';
import { agentReleaseState, newerRelease, UpdateAgentDialog } from '../devices/UpdateAgentDialog';
import { EffectivePolicyPanel, PolicyDraftPanel } from '../policies/PolicyDraftPanel';
import { ago, minuteFloor } from '../lib/time';
import { ApprovalDialog, useCapabilities } from '../operations/jobs';
import { deviceSummary, DeviceFacts, NotVisible } from '../records/DeviceDetails';
import { recordPath } from '../records/paths';
import { useScope } from '../scope/scope';
import { RemoteInButton, RemotePanel } from '../remote/RemotePanel';
import { needsLook, needsLookQuery, useAgentReleases, useCatalogRunbooks, useDeviceJobs, useFacets, useRecord, useRemoteStatus, useSearch, useTelemetry } from '../search/hooks';
import { emptyFilters, writeFilters } from '../search/state';
import { buttonClass } from '../ui/Button';
import { cn } from '../ui/cn';
import { ErrorPanel } from '../ui/errors';
import { Card, CopyButton, SectionTitle, Skeleton, Spark, TimeAgo, useNow } from '../ui/misc';
import { StatusBadge } from '../ui/status';
import { PageHeader } from './PageHeader';

const TABS = [['overview', 'Overview'], ['performance', 'Performance'], ['storage', 'Storage'], ['software', 'Software'], ['activity', 'Activity'], ['powershell', 'PowerShell'],
  ['remote', 'Remote']] as const;
type Tab = typeof TABS[number][0];

// Keyed by id: requests keep their last answer while the next loads, and one device's peers or
// telemetry must never show as another's. A refresh of the same device still keeps its data on screen.
export function DevicePage() {
  const { id } = useParams();
  return <Device key={id} />;
}

function Device() {
  const { id } = useParams();
  const { mspId, remember } = useScope();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find(([key]) => key === params.get('tab'))?.[0] ?? 'overview') as Tab;
  const setTab = (next: Tab) => setParams(p => { const out = new URLSearchParams(p); if (next === 'overview') out.delete('tab'); else out.set('tab', next); return out; }, { replace: true });
  const [range, setRange] = useState<'24h' | '7d'>('24h');
  const now = useNow();
  const record = useRecord('device', id);
  const device = record.data;
  const telemetry = useTelemetry(device?.managed ? id : undefined, range);
  const catalog = useCatalogRunbooks();
  const jobs = useDeviceJobs(device?.managed ? id : undefined);
  const remote = useRemoteStatus(device?.managed ? id : undefined);
  const caps = useCapabilities(device ?? null);
  // Marking findings benign or resolved (WRMM-160) is for those who work them: running diagnostics or approving jobs.
  const canReview = caps.diagnostics || caps.approve;
  const [running, setRunning] = useState<{ runbook: RunbookSummary; reason: string } | null>(null);
  const [approving, setApproving] = useState<JobView | null>(null);
  const [updating, setUpdating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const releases = useAgentReleases(!!device?.managed && (device.os === 'windows' || device.os === 'linux' || device.os === 'macos') && caps.enroll);
  useEffect(() => { if (device) remember(device); }, [device, remember]);
  // When a job finishes, re-read telemetry so the fix's verification shows without waiting for the next poll.
  const client = useQueryClient();
  const finished = (jobs.data ?? []).filter(j => ['succeeded', 'failed', 'unknown_outcome'].includes(j.state)).map(j => j.id).join(',');
  useEffect(() => {
    if (!finished) return;
    const refresh = () => client.invalidateQueries({ queryKey: ['scope', mspId, 'telemetry', id] });
    void refresh();
    const later = setTimeout(() => void refresh(), 4000); // The device reports its new inventory just after the result.
    return () => clearTimeout(later);
  }, [finished, client, mspId, id]);
  const siteQuery = device?.siteId ? { version: 1 as const, kinds: ['device' as const], limit: 12, sort: { field: 'status' as const, direction: 'asc' as const },
    filters: [{ mode: 'and' as const, clauses: [{ field: 'siteId' as const, op: 'in' as const, values: [device.siteId] }] }] } : null;
  const neighbours = useSearch(siteQuery);

  if (record.isError) return <Card><ErrorPanel error={record.error} onRetry={() => void record.refetch()} /></Card>;
  if (!device) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>;
  const t = telemetry.data;
  const os = device.os;
  const runbooks = (catalog.data ?? []).filter(r => r.os.includes(os as 'windows'));
  const summary = deviceSummary(device, now);
  const others = (neighbours.data?.hits ?? []).filter(h => h.id !== device.id);
  const pendingApprovals = (jobs.data ?? []).filter(j => j.state === 'awaiting_approval');
  const agentVersion = t?.facts?.agent.version;
  const agentArchitecture = t?.facts?.agent.architecture ?? (device.os === 'windows' ? 'x64' : t?.facts?.os.architecture);
  const reinstallArm = windowsArmReinstallRequired(device.os, t?.facts);
  // A compatible pending update or any in-flight update holds this device; an old foreign queue does not.
  const updateOpen = (jobs.data ?? []).some(j => j.operation === 'agent_update' && (['delivered', 'running'].includes(j.state)
    || (['awaiting_approval', 'queued'].includes(j.state) && j.architecture === (agentArchitecture ?? 'x64'))));
  // The release list is cached workspace-wide; offer it only where this person may manage the device's agent.
  const release = reinstallArm || updateOpen || !caps.enroll ? null : newerRelease(releases.data?.filter(r => r.outdatedDeviceIds.includes(device.id)), agentVersion);
  // Said outright, so nobody looks for an update button that isn't needed.
  const agentState = reinstallArm ? 'reinstall-arm64' : !caps.enroll ? undefined
    : agentReleaseState(releases.isError ? undefined : releases.data, agentVersion, device.os, agentArchitecture, updateOpen);
  const ask = (q: string) => `/w/${mspId}/ask?${new URLSearchParams({ q, device: device.id })}`;
  const similar = writeFilters({ ...emptyFilters, kind: 'device', os: device.os ? [device.os as 'windows'] : ['unknown'], status: device.status ? [device.status as 'healthy'] : ['unknown'] }, { kind: 'device' });
  // The Remote tab appears once this server is known to have WITTCO Remote.
  const remoteSetUp = !!remote.data && remote.data.state !== 'not_configured';
  const visibleTabs = device.managed ? TABS.filter(([key]) => key !== 'remote' || remoteSetUp) : TABS.filter(([key]) => key === 'overview');
  // A bookmarked tab the device can't offer (no agent) falls back to Overview rather than showing tools that can't work.
  const active: Tab = visibleTabs.some(([key]) => key === tab) ? tab : 'overview';

  return (
    <div className="space-y-5">
      <PageHeader mono title={device.title}
        eyebrow={<span className="flex flex-wrap items-center gap-1.5">
          <Link to={`/w/${mspId}/devices`} className="hover:text-ink">Devices</Link><ChevronRight className="size-3.5" />
          {device.organizationName && <><Link to={recordPath(mspId, 'organization', device.organizationId)} className="hover:text-ink">{device.organizationName}</Link><ChevronRight className="size-3.5" /></>}
          {device.siteId && device.siteName && <Link to={recordPath(mspId, 'site', device.siteId)} className="hover:text-ink">{device.siteName}</Link>}
        </span>}
        badge={<span className="flex flex-wrap items-center gap-2"><StatusBadge status={device.status} />
          {device.managed ? <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-3"><span className={cn('size-1.5 rounded-full', device.status === 'offline' ? 'bg-ink-3' : 'bg-healthy')} />Agent · {device.lastSeenAt ? `checked in ${ago(device.lastSeenAt, now)}` : 'not yet reporting'}</span>
            : <span className="inline-flex items-center gap-1 rounded-md bg-sunken px-2 py-0.5 text-[12px] text-ink-3"><PlugZap className="size-3.5" />No agent</span>}
          <CopyButton value={device.hostname ?? device.title} label="Copy hostname" /><DeviceIdentity device={device} /></span>}
        actions={<>
          {device.managed && <RemoteInButton device={device} status={remote.data} />}
          {device.managed && runbooks.length > 0 && caps.diagnostics && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className={buttonClass('secondary')}><Play className="size-4 text-series" />Run</DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={6} className="z-50 max-h-[70vh] w-80 overflow-y-auto rounded-xl border border-line bg-raised p-1 shadow-pop animate-pop">
                  {(['read', 'change'] as const).map(effect => (
                    <DropdownMenu.Group key={effect}>
                      <DropdownMenu.Label className="px-2.5 pb-1 pt-2 text-[12px] font-medium text-ink-3">{effect === 'read' ? 'Look (read-only, runs now)' : 'Fix (needs approval)'}</DropdownMenu.Label>
                      {runbooks.filter(r => r.effect === effect).map(r => (
                        <DropdownMenu.Item key={r.id} disabled={!!runbookUnavailableReason(r, t?.facts?.agent)} onSelect={() => setRunning({ runbook: r, reason: r.title })} className="cursor-pointer rounded-lg px-2.5 py-2 outline-none data-[highlighted]:bg-hover data-[disabled]:opacity-60">
                          <span className="block text-[13.5px] font-medium">{r.title}</span><span className="block text-[12px] text-ink-3">{runbookUnavailableReason(r, t?.facts?.agent) ?? r.summary}</span>
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Group>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
          {device.managed && (os === 'windows' || os === 'linux' || os === 'macos') && caps.scripts && <button type="button" onClick={() => setTab('powershell')} className={buttonClass('secondary')}><Terminal className="size-4" />{os === 'linux' || os === 'macos' ? 'Shell' : 'PowerShell'}</button>}
          <Link to={ask(`Investigate ${device.title}`)} className={buttonClass('primary')}><Sparkles className="size-4 text-brand" />Investigate</Link>
          {caps.enroll && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className={buttonClass('ghost', 'icon')} aria-label="More actions"><MoreHorizontal className="size-4" /></DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={6} className="z-50 w-56 rounded-xl border border-line bg-raised p-1 shadow-pop animate-pop">
                  <DropdownMenu.Item onSelect={() => setRemoving(true)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13.5px] text-offline outline-none data-[highlighted]:bg-hover">
                    <Trash2 className="size-4" />Remove device…
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </>} />

      <DeviceName device={device} />

      {pendingApprovals.length > 0 && caps.approve && (
        <button type="button" onClick={() => setApproving(pendingApprovals[0]!)}
          className="flex w-full items-center gap-3 rounded-xl border border-attention/40 bg-attention-soft/60 px-4 py-3 text-left text-[13.5px] hover:bg-attention-soft">
          <span className="font-semibold">{pendingApprovals.length} job{pendingApprovals.length === 1 ? '' : 's'} on this device need{pendingApprovals.length === 1 ? 's' : ''} approval</span>
          <span className="text-ink-2">· {pendingApprovals[0]!.title}</span><span className="flex-1" /><span className="font-medium">Review →</span>
        </button>
      )}

      {visibleTabs.length > 1 && (
        <div role="tablist" aria-label="Device sections" className="-mx-1 flex gap-1 overflow-x-auto border-b border-line px-1 [scrollbar-width:none]">
          {visibleTabs.map(([key, label]) => (
            <button key={key} role="tab" type="button" aria-selected={active === key} onClick={() => setTab(key)}
              className={cn('-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-[13.5px] font-medium transition-colors',
                active === key ? 'border-brand text-ink' : 'border-transparent text-ink-3 hover:text-ink')}>
              {key === 'powershell' && (os === 'linux' || os === 'macos') ? 'Shell' : label}{key === 'overview' && t && t.signals.open.length > 0 && <span className="rounded-full bg-attention-soft px-1.5 text-[11px] font-semibold text-attention">{t.signals.open.length}</span>}
            </button>
          ))}
        </div>
      )}

      {telemetry.isError && <Card><ErrorPanel error={telemetry.error} onRetry={() => void telemetry.refetch()} /></Card>}

      {active === 'overview' && (
        <div className="space-y-5">
          <div className={cn('rounded-xl border px-5 py-4', summary.tone === 'warn' ? 'border-attention/30 bg-attention-soft/40' : 'border-line bg-panel')}>
            <p className="text-[15px] leading-relaxed">{summary.text}</p>
            {device.siteId && <SiteHint key={device.siteId} device={device} />}
          </div>
          {!device.managed ? (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
              <Card className="px-5 py-3"><DeviceFacts hit={device} /></Card>
              <div className="space-y-4">
                <Card className="p-4">
                  <SectionTitle>No agent on this device</SectionTitle>
                  <p className="mt-2 text-[13px] leading-relaxed text-ink-2">This is an inventory record only. Live health, metrics, software and remote actions need the WITTCO agent; its record will then update itself.</p>
                  <Link to={`/w/${mspId}/settings#enrollment`} className={buttonClass('secondary', 'sm', 'mt-3')}>Add devices</Link>
                </Card>
                <NotVisible />
              </div>
            </div>
          ) : !t ? <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-28" />)}</div> : (
            <>
              <Vitals t={t} />
              {t.signals.open.length > 0 ? (
                <section className="space-y-2">
                  <SectionTitle>Needs attention</SectionTitle>
                  <div className="space-y-3">
                    {t.signals.open.map(s => (
                      <SignalCard key={s.id} signal={s} runbooks={runbooks.filter(r => r.addresses.includes(s.key))} canRun={caps.diagnostics}
                        onRun={(runbook, reason) => setRunning({ runbook, reason })} footer={<ReviewControls deviceId={device.id} signal={s} canReview={canReview} />} />
                    ))}
                  </div>
                </section>
              ) : (
                <Card className="flex items-center gap-3 p-4"><span className="size-2 rounded-full bg-healthy" />
                  <p className="text-[13.5px]"><span className="font-semibold">No open issues.</span> <span className="text-ink-2">{t.signals.reviewed.length
                    ? 'Everything else in the latest report looks normal; the findings below were reviewed.'
                    : 'Disk, memory, CPU, updates and protection all look normal in the latest report.'}</span></p></Card>
              )}
              <ReviewedList deviceId={device.id} signals={t.signals.reviewed} canReview={canReview} />
              <ResolvedList signals={t.signals.resolved} />
              <FactsPanel t={t} update={release && { version: release.version, open: () => setUpdating(true) }} agent={agentState} />
            </>
          )}
          {device.managed && <DeviceActivityPreview deviceId={device.id} onHistory={() => setTab('activity')} />}
          {device.organizationId && device.siteId && <EffectivePolicyPanel target={{ kind: 'device', organizationId: device.organizationId, siteId: device.siteId, deviceId: device.id }} />}
          {device.organizationId && device.siteId && <PolicyDraftPanel target={{ kind: 'device', organizationId: device.organizationId, siteId: device.siteId, deviceId: device.id }} />}
          {device.siteId && (
            <section className="space-y-2">
              <SectionTitle aside={<Link to={recordPath(mspId, 'site', device.siteId)} className="text-[12.5px] text-ink-3 hover:text-ink">{neighbours.data ? `All ${neighbours.data.total} at this site` : 'Open site'}</Link>}>
                Also at {device.organizationName} · {device.siteName}
              </SectionTitle>
              <Card className="divide-y divide-line overflow-hidden">
                {neighbours.isPending ? <div className="p-4"><Skeleton className="w-1/2" /></div>
                  // A failed lookup is not evidence that the device is alone.
                  : neighbours.isError ? <ErrorPanel error={neighbours.error} onRetry={() => void neighbours.refetch()} />
                  : !others.length ? <p className="px-4 py-3 text-ink-3">No other devices at this site.</p>
                  : others.slice(0, 8).map(h => (
                    <Link key={h.id} to={recordPath(mspId, 'device', h.id)} onClick={() => remember(h)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-hover">
                      <StatusBadge status={h.status} compact className="w-24" />
                      <span className="min-w-0 flex-1 truncate"><span className="font-mono text-[13px] font-medium">{h.title}</span>
                        {h.signals[0] && <span className="ml-2 text-[12.5px] text-ink-3">{h.signals[0].title}</span>}</span>
                      <TimeAgo value={h.lastSeenAt} className="text-[13px]" />
                    </Link>
                  ))}
              </Card>
              <Link to={`/w/${mspId}/devices?${similar}`} className="inline-flex items-center gap-1.5 px-1 text-[12.5px] font-medium text-ink-3 hover:text-ink"><Layers className="size-3.5" />Devices with the same OS and status</Link>
            </section>
          )}
        </div>
      )}
      {active === 'performance' && t && <PerformancePanel t={t} range={range} onRange={setRange} />}
      {active === 'storage' && t && <StoragePanel t={t} />}
      {active === 'software' && <SoftwarePanel deviceId={device.id} facts={t?.facts} collectedAt={t?.collectedAt} />}
      {active === 'activity' && <ActivityPanel deviceId={device.id} t={t} onApprove={setApproving} />}
      {active === 'powershell' && <PowerShellPanel device={device} os={os} presence={t?.facts?.presence} agent={t?.facts?.agent} />}
      {active === 'remote' && <RemotePanel device={device} status={remote.data} />}
      {active !== 'overview' && !t && device.managed && telemetry.isPending && <Skeleton className="h-64 w-full" />}

      <RunbookDialog runbook={running?.runbook ?? null} reason={running?.reason} device={device} agent={t?.facts?.agent}
        open={!!running} onOpenChange={o => { if (!o) setRunning(null); }} onNeedsApproval={setApproving} />
      {agentVersion && <UpdateAgentDialog release={release} running={agentVersion} device={device} open={updating} onOpenChange={setUpdating} onNeedsApproval={setApproving} />}
      <ApprovalDialog job={approving} open={!!approving} onOpenChange={o => { if (!o) setApproving(null); }} />
      {caps.enroll && <RemoveDeviceDialog device={device} open={removing} onOpenChange={setRemoving} />}
    </div>
  );
}

/**
 * Exact counts for the whole site, not the first (most troubled) page. Keyed by site so a count
 * kept from another site never shows here; while either count is missing or failed it says nothing.
 */
function SiteHint({ device }: { device: SearchHit }) {
  const scope = { mode: 'and' as const, clauses: [{ field: 'siteId' as const, op: 'in' as const, values: [device.siteId!] }] };
  const site = useFacets({ version: 1, kinds: ['device'], filters: [scope] });
  // The same definition as Today: a reported problem, no status, or no check-in for a week.
  const asOf = minuteFloor();
  const look = needsLookQuery(asOf);
  const needs = useSearch({ ...look, limit: 1, filters: [...look.filters!, scope] });
  if (!site.isSuccess || !needs.isSuccess) return null;
  const peers = site.data.total - 1;
  const self = needsLook(device, asOf); // Only subtract this device if the count includes it (an offline workstation isn't).
  const troubled = Math.min(peers, needs.data.total - (self ? 1 : 0)); // Two requests can disagree by a device enrolled in between.
  if (troubled < 2 || troubled / peers < 0.3) return null;
  return (
    <p className="mt-2 flex items-start gap-2 text-[13.5px] text-ink-2"><Spark className="mt-0.5 size-3.5 shrink-0" />
      {troubled} of {peers} other devices at {device.siteName} also need a look — check whether this is site-wide before working on the device alone.</p>
  );
}
